// Auto-scroll behavior for the Massimo chat panel:
// - On new user message, scroll to bottom (always)
// - On new assistant message, scroll to top of message (only if near bottom)
// - Save scroll position per chat in sessionStorage
// - On page load, restore saved position or scroll to bottom
// - Show a scroll-to-bottom button (with an unread count) when scrolled away from the bottom
// - Jump to a specific message on request (drives the transcript outline)
import { Controller } from "@hotwired/stimulus";

const NEAR_BOTTOM_THRESHOLD = 20;

export default class extends Controller {
  static targets = ["viewport", "toBottomButton", "unreadBadge"];

  #isNearBottom = true;
  #initialLoad = true;
  #observer = null;
  #handleScroll = null;
  #deepestSeenId = null;
  #lastMessageIds = [];
  #scrollRaf = null;

  connect() {
    this.#setupScrollHandler();
    this.#setupMutationObserver();
    this.#updateToBottomButton();

    requestAnimationFrame(() => {
      this.#initialLoad = false;
    });
  }

  disconnect() {
    this.#observer?.disconnect();
    this.viewportTarget.removeEventListener("scroll", this.#handleScroll);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
  }

  // Scrolls to the bottom and re-engages auto-follow. Wired to the
  // scroll-to-bottom button via a Stimulus action.
  scrollToBottom() {
    this.#animateScrollTo(this.viewportTarget.scrollHeight, 150);
    this.#isNearBottom = true;
    // Mark everything seen so the badge clears immediately and doesn't flash the stale
    // count back while the animated scroll is still in flight.
    this.#deepestSeenId = this.#lastMessageIds.at(-1) ?? this.#deepestSeenId;
    this.#updateToBottomButton();
    this.#updateUnreadBadge(0);
  }

  // Smooth-scrolls a specific message to the anchor position. Driven by the
  // transcript outline via the `massimo:scroll-to-message` window event.
  scrollToMessage(id) {
    const el = this.viewportTarget.querySelector(`#${CSS.escape(id)}`);
    if (!el) return;
    // Re-evaluate the target each frame so the animation tracks the row as content above
    // settles; hold position if the row is replaced (detached) mid-scroll.
    this.#animateScrollTo(
      () => (el.isConnected ? this.#topOffsetFor(el) : this.viewportTarget.scrollTop),
      300
    );
  }

  onScrollToMessage({ detail }) {
    this.scrollToMessage(detail.id);
  }

  // Tracks the reader's position (from massimo--message-visibility) to show a
  // count of new messages that arrived below the fold while scrolled away.
  onVisibilityChange({ detail }) {
    const { messageIds, visibleMessageIds } = detail;
    this.#lastMessageIds = messageIds;
    if (visibleMessageIds.length > 0) {
      const lastVisible = visibleMessageIds[visibleMessageIds.length - 1];
      if (messageIds.indexOf(lastVisible) > this.#seenIndex(messageIds))
        this.#deepestSeenId = lastVisible;
    }
    // Near the bottom there's nothing unread below the fold (also avoids a transient count
    // before the IntersectionObserver reports the first visible rows). A negative seenIndex
    // means no live watermark — either we haven't seen anything yet, or a previously-seen
    // message was removed/regenerated — so treat it as caught up rather than counting the
    // whole transcript as unread (the next visibility event re-anchors the watermark).
    const seenIndex = this.#seenIndex(messageIds);
    const count =
      this.#isNearBottom || seenIndex < 0 ? 0 : Math.max(0, messageIds.length - 1 - seenIndex);
    this.#updateUnreadBadge(count);
  }

  // Tracks whether the user is near the bottom of the container and
  // persists the scroll position to sessionStorage on every scroll event.
  #setupScrollHandler() {
    this.#handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = this.viewportTarget;
      this.#isNearBottom = scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_THRESHOLD;
      this.#savedPosition = scrollTop;
      this.#updateToBottomButton();
    };
    this.viewportTarget.addEventListener("scroll", this.#handleScroll);
  }

  // Watches for DOM changes (new messages via Turbo Streams). During initial
  // load, restores saved position or scrolls to bottom. After that, scrolls to
  // bottom for user messages (always) or to the last message for assistant
  // messages (only if near bottom).
  #setupMutationObserver() {
    this.#observer = new MutationObserver(() => {
      if (this.#initialLoad) {
        const saved = this.#savedPosition;
        if (saved !== null) {
          this.viewportTarget.scrollTop = saved;
          const { scrollTop, scrollHeight, clientHeight } = this.viewportTarget;
          this.#isNearBottom = scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_THRESHOLD;
        } else {
          this.viewportTarget.scrollTop = this.viewportTarget.scrollHeight;
        }
      } else if (this.#lastMessageIsFromUser) {
        this.#animateScrollTo(this.viewportTarget.scrollHeight, 150);
      } else if (this.#isNearBottom) {
        this.#scrollToLastMessage();
      }
      this.#updateToBottomButton();
    });
    this.#observer.observe(this.viewportTarget, { childList: true, subtree: true });
  }

  // Shows the scroll-to-bottom button only when the user has scrolled away
  // from the bottom. Safe to call on views that don't render the button.
  #updateToBottomButton() {
    if (!this.hasToBottomButtonTarget) return;
    const hidden = this.#isNearBottom;
    this.toBottomButtonTarget.classList.toggle("opacity-0", hidden);
    this.toBottomButtonTarget.classList.toggle("pointer-events-none", hidden);
    // Keep the opacity transition, but take it out of the tab order / make it inert when
    // hidden (at opacity 0 it would otherwise stay keyboard-focusable and clickable).
    this.toBottomButtonTarget.disabled = hidden;
  }

  // Renders the unread-message count on the scroll-to-bottom button.
  #updateUnreadBadge(count) {
    if (!this.hasUnreadBadgeTarget) return;
    this.unreadBadgeTarget.textContent = count > 0 ? String(count) : "";
    this.unreadBadgeTarget.classList.toggle("hidden", count === 0);
  }

  #seenIndex(messageIds) {
    return this.#deepestSeenId ? messageIds.indexOf(this.#deepestSeenId) : -1;
  }

  get #storageKey() {
    const chatId = sessionStorage.getItem("massimo-chat-id");
    if (chatId) return `massimo-scroll-position-${chatId}`;
    return null;
  }

  get #savedPosition() {
    const key = this.#storageKey;
    if (!key) return null;
    const value = sessionStorage.getItem(key);
    return value !== null ? Number(value) : null;
  }

  set #savedPosition(value) {
    const key = this.#storageKey;
    if (key) sessionStorage.setItem(key, value);
  }

  get #lastMessageIsFromUser() {
    let lastChild = this.viewportTarget.lastElementChild;
    if (lastChild?.id === "massimo-thinking") {
      lastChild = lastChild.previousElementSibling;
    }
    return lastChild?.classList.contains("flex-row-reverse") ?? false;
  }

  // requestAnimationFrame ease-out scroll, instead of native smooth scrolling so we
  // control the speed. `target` may be a number or a function re-evaluated each frame
  // (to track a moving destination). Supersedes any in-flight animation so two scrolls
  // never fight over scrollTop.
  #animateScrollTo(target, duration) {
    const resolve = typeof target === "function" ? target : () => target;
    const start = this.viewportTarget.scrollTop;
    const startTime = performance.now();
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease = progress * (2 - progress);
      this.viewportTarget.scrollTop = start + (resolve() - start) * ease;
      this.#scrollRaf = progress < 1 ? requestAnimationFrame(step) : null;
    };

    this.#scrollRaf = requestAnimationFrame(step);
  }

  #scrollToLastMessage() {
    const el = this.viewportTarget.lastElementChild;
    // Native smooth scroll (not #animateScrollTo) so a user scroll mid-animation
    // interrupts it, instead of the rAF loop fighting the user's scrollTop.
    if (el) this.viewportTarget.scrollTo({ top: this.#topOffsetFor(el), behavior: "smooth" });
  }

  // scrollTop that places the top of `el` 20px below the top of the viewport.
  #topOffsetFor(el) {
    const containerTop = this.viewportTarget.getBoundingClientRect().top;
    return el.getBoundingClientRect().top - containerTop + this.viewportTarget.scrollTop - 20;
  }
}
